import { useEffect, useState } from "react"
import { Loader2, Paperclip, Download, Eye, Save } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"
import { getSignedUrl } from "@/services/api/reports"
import { toast } from "@/components/ui/use-toast"
import { AddReportFromChatDialog } from "./AddReportFromChatDialog"

export function FileViewer({
  filePath,
  fileType,
  fileName,
  messageId,
  patientId,
  children,
  previewClassName = "",
  dialogClassName = "",
}) {
  const [open, setOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [signedUrl, setSignedUrl] = useState(null)
  const [showReportDialog, setShowReportDialog] = useState(false)

  // Fetch signed URL when dialog opens
  useEffect(() => {
    const fetchSignedUrl = async () => {
      if (!open) return

      try {
        setIsLoading(true)
        const response = await getSignedUrl(filePath)
        setSignedUrl(response.data.data.public_link[0])
      } catch (err) {
        toast({
          title: "Error",
          description: "Failed to load file",
          variant: "destructive",
        })
        setOpen(false)
      } finally {
        setIsLoading(false)
      }
    }

    if (open) {
      fetchSignedUrl()
    }
  }, [open, filePath])

  const handleDownload = async () => {
    try {
      setIsLoading(true)
      const response = await getSignedUrl(filePath)
      const url = response.data.data.public_link[0]

      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", fileName)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      toast({
        title: "Success",
        description: "File download started",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to download file",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const isImage = fileType?.includes("image")
  const isPdf =
    fileType?.includes("pdf") || fileName?.toLowerCase().endsWith(".pdf")

  const renderActionButtons = () => {
    return (
      <div className="flex gap-2">
        <DialogTrigger asChild>
          {isImage ? (
            <Button
              variant="outline"
              className="flex-1 text-blue-600 hover:text-blue-800"
            >
              <Eye className="h-4 w-4 mr-2" />
              View Image
            </Button>
          ) : (
            <Button
              variant="outline"
              className="flex-1 text-blue-600 hover:text-blue-800"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleDownload()
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </>
              )}
            </Button>
          )}
        </DialogTrigger>

        {patientId && messageId && (
          <Button
            variant="outline"
            className="text-emerald-600 hover:text-emerald-800 border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              setShowReportDialog(true)
            }}
          >
            <Save className="h-4 w-4 mr-2" />
            Save as Report
          </Button>
        )}
      </div>
    )
  }

  // Only render Dialog for images
  if (!isImage) {
    return (
      <>
        {renderActionButtons()}
        {patientId && messageId && (
          <AddReportFromChatDialog
            open={showReportDialog}
            onOpenChange={setShowReportDialog}
            messageId={messageId}
            patientId={patientId}
            fileName={fileName}
          />
        )}
      </>
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {renderActionButtons()}
        <DialogContent className={cn("", dialogClassName)}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              {fileName}
            </DialogTitle>
          </DialogHeader>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-[60vh] gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
              <p className="text-sm text-gray-500">Loading image...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="relative flex items-center justify-center bg-gray-50 rounded-lg p-4">
                <img
                  src={signedUrl}
                  alt={fileName}
                  className={cn(
                    "max-w-full h-auto max-h-[70vh] object-contain rounded-lg",
                    previewClassName
                  )}
                  onError={() => {
                    toast({
                      title: "Error",
                      description: "Failed to load image",
                      variant: "destructive",
                    })
                    setOpen(false)
                  }}
                />
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleDownload}
                  disabled={isLoading}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {patientId && messageId && (
        <AddReportFromChatDialog
          open={showReportDialog}
          onOpenChange={setShowReportDialog}
          messageId={messageId}
          patientId={patientId}
          fileName={fileName}
        />
      )}
    </>
  )
}
